import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  warehouseOrders,
  type WarehouseOrderDirection,
  type WarehouseOrderItem,
} from '@/api/warehouseOrders';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import DatePopover from '@/components/DatePopover';
import { useConfirm } from '@/components/ConfirmProvider';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';

interface ItemRow {
  id?: string;
  line_number: number;
  nomenclature_id: string;
  expected_quantity: string;
  actual_quantity: string;
  unit_price: string;
  variance_reason: string;
  line_comment: string;
  variance_quantity?: string;
}

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  comment: string;
  direction: WarehouseOrderDirection;
  warehouse_id: string;
  contractor_id: string;
  contract_id: string;
  committee_chairperson_id: string;
  items: ItemRow[];
}

const EMPTY: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  comment: '',
  direction: 'IN',
  warehouse_id: '',
  contractor_id: '',
  contract_id: '',
  committee_chairperson_id: '',
  items: [],
};

const newRow = (n: number): ItemRow => ({
  line_number: n,
  nomenclature_id: '',
  expected_quantity: '0',
  actual_quantity: '',
  unit_price: '',
  variance_reason: '',
  line_comment: '',
});

function extractError(e: unknown): string {
  const err = e as { response?: { data?: { detail?: unknown } } };
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => (d as { msg?: string }).msg ?? '').join('; ');
  return 'Помилка';
}

export default function WarehouseOrderEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const { availableOrgs } = usePermissionsByOrg('documents.warehouse_orders.create');

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const contractorsList = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 1000 }),
  });
  const nomList = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.list({ limit: 500 }),
  });
  const docQuery = useQuery({
    queryKey: ['warehouse-order', id],
    queryFn: () => warehouseOrders.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refs.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) =>
        f.organization_id
          ? f
          : { ...f, organization_id: firstOrg, items: f.items.length ? f.items : [newRow(1)] },
      );
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        comment: d.comment ?? '',
        direction: d.direction,
        warehouse_id: d.warehouse_id,
        contractor_id: d.contractor_id ?? '',
        contract_id: d.contract_id ?? '',
        committee_chairperson_id: d.committee_chairperson_id ?? '',
        items: d.items.map((it: WarehouseOrderItem) => ({
          id: it.id,
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          expected_quantity: String(it.expected_quantity),
          actual_quantity: it.actual_quantity != null ? String(it.actual_quantity) : '',
          unit_price: it.unit_price != null ? String(it.unit_price) : '',
          variance_reason: it.variance_reason ?? '',
          line_comment: it.line_comment ?? '',
          variance_quantity: it.variance_quantity != null ? String(it.variance_quantity) : '0',
        })),
      });
    }
  }, [isNew, docQuery.data, availableOrgs]);

  const isDraft = isNew || (docQuery.data?.state === 'DRAFT');
  const isCommitteeVerified = docQuery.data?.state === 'COMMITTEE_VERIFIED';
  const isExecuted = docQuery.data?.state === 'EXECUTED';
  const isCancelled = docQuery.data?.state === 'CANCELLED';
  const isPosted = !!docQuery.data?.posted;

  const create = useMutation({
    mutationFn: () =>
      warehouseOrders.create({
        organization_id: form.organization_id,
        document_number: form.document_number || null,
        document_date: new Date(form.document_date).toISOString(),
        comment: form.comment || null,
        direction: form.direction,
        warehouse_id: form.warehouse_id,
        contractor_id: form.contractor_id || null,
        contract_id: form.contract_id || null,
        committee_chairperson_id: form.committee_chairperson_id || null,
        items: form.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          expected_quantity: it.expected_quantity,
          actual_quantity: it.actual_quantity || null,
          unit_price: it.unit_price || null,
          variance_reason: it.variance_reason || null,
          line_comment: it.line_comment || null,
        })),
      }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['warehouse-orders'] });
      navigate(`/warehouse/orders/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });

  const verifyMut = useMutation({
    mutationFn: () => warehouseOrders.verify(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warehouse-order', id] });
      qc.invalidateQueries({ queryKey: ['warehouse-orders'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const executeMut = useMutation({
    mutationFn: () => warehouseOrders.execute(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warehouse-order', id] });
      qc.invalidateQueries({ queryKey: ['warehouse-orders'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const cancelMut = useMutation({
    mutationFn: (reason: string) => warehouseOrders.cancel(id!, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warehouse-order', id] });
      qc.invalidateQueries({ queryKey: ['warehouse-orders'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const postMut = useMutation({
    mutationFn: () => warehouseOrders.post(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warehouse-order', id] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const updateItemMut = useMutation({
    mutationFn: ({ itemId, patch }: { itemId: string; patch: Partial<ItemRow> }) =>
      warehouseOrders.updateItem(id!, itemId, {
        actual_quantity: patch.actual_quantity ?? undefined,
        variance_reason: patch.variance_reason ?? undefined,
        line_comment: patch.line_comment ?? undefined,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['warehouse-order', id] }),
    onError: (e) => setError(extractError(e)),
  });

  const title = useMemo(() => {
    const dirLabel = form.direction === 'IN' ? 'Приходний ордер' : 'Видатковий ордер';
    if (isNew) return `Новий ${dirLabel.toLowerCase()}`;
    return `${dirLabel}: ${form.document_number}`;
  }, [isNew, form.direction, form.document_number]);

  function setItem(idx: number, patch: Partial<ItemRow>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, i) => (i === idx ? { ...e, ...patch } : e)),
    }));
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>{title}</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/warehouse/orders')}>Скасувати</button>
          {isNew && (
            <button
              className="primary"
              onClick={() => create.mutate()}
              disabled={create.isPending}
            >
              Зберегти
            </button>
          )}
          {!isNew && isDraft && (
            <button
              className="primary"
              onClick={() => verifyMut.mutate()}
              disabled={verifyMut.isPending}
              data-testid="wo-verify-btn"
            >
              ✓ Перевірити комітетом
            </button>
          )}
          {!isNew && isCommitteeVerified && (
            <button
              className="primary"
              onClick={() => executeMut.mutate()}
              disabled={executeMut.isPending}
              data-testid="wo-execute-btn"
            >
              ▶ Виконати
            </button>
          )}
          {!isNew && (isDraft || isCommitteeVerified) && (
            <button
              className="danger"
              onClick={() =>
                confirm('Скасувати ордер? Вкажіть причину.').then((ok) => {
                  if (ok) {
                    const reason = window.prompt('Причина скасування:') ?? '';
                    if (reason.length >= 3) cancelMut.mutate(reason);
                  }
                })
              }
            >
              ✗ Скасувати
            </button>
          )}
          {!isNew && isExecuted && !isPosted && (
            <button
              className="primary"
              onClick={() => postMut.mutate()}
              disabled={postMut.isPending}
            >
              ▶ Провести
            </button>
          )}
        </div>
      </div>

      {error && (
        <div style={{ color: '#b91c1c', padding: '0.5rem' }}>{error}</div>
      )}

      {!isNew && docQuery.data && (
        <div style={{ padding: '0.5rem', fontSize: '0.875rem' }}>
          <strong>Стан:</strong> {docQuery.data.state}
          {isPosted && <span style={{ marginLeft: '1rem', color: '#15803d' }}>✓ проведено</span>}
          {isCancelled && (
            <span style={{ marginLeft: '1rem', color: '#b91c1c' }}>
              Причина: {docQuery.data.cancellation_reason}
            </span>
          )}
          {isExecuted && docQuery.data.related_goods_receipt_id && (
            <div>
              ✓ Створено приходний документ:{' '}
              <code>{docQuery.data.related_goods_receipt_id}</code>
            </div>
          )}
          {isExecuted && docQuery.data.related_goods_sale_id && (
            <div>
              ✓ Створено видатковий документ:{' '}
              <code>{docQuery.data.related_goods_sale_id}</code>
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Напрямок</label>
          <select
            value={form.direction}
            onChange={(e) =>
              setForm({ ...form, direction: e.target.value as WarehouseOrderDirection })
            }
            disabled={!isNew}
            data-testid="wo-direction-select"
          >
            <option value="IN">Приходний (від постачальника)</option>
            <option value="OUT">Видатковий (покупцю)</option>
          </select>
        </div>
        <div className="field">
          <label>Організація</label>
          <select
            value={form.organization_id}
            onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            disabled={!isNew}
          >
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.code} — {o.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>№ документа</label>
          <input
            value={form.document_number}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
            disabled={!isDraft}
          />
        </div>
        <div className="field">
          <label>Дата</label>
          <DatePopover
            withTime
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={!isDraft}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Склад</label>
          <select
            value={form.warehouse_id}
            onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}
            disabled={!isDraft}
          >
            <option value="">—</option>
            {warehousesQ.data?.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Контрагент</label>
          <select
            value={form.contractor_id}
            onChange={(e) => setForm({ ...form, contractor_id: e.target.value })}
            disabled={!isDraft}
          >
            <option value="">— (standalone)</option>
            {contractorsList.data?.items.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Коментар</label>
          <input
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            disabled={!isDraft}
          />
        </div>
      </div>

      <h3 style={{ marginTop: '1rem' }}>Позиції</h3>
      <table className="table" data-testid="wo-items-table">
        <thead>
          <tr>
            <th>№</th>
            <th>Номенклатура</th>
            <th>Очікувано</th>
            <th>Фактично</th>
            <th>Розбіжність</th>
            <th>Причина</th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => {
            const variance = Number(it.actual_quantity || 0) - Number(it.expected_quantity || 0);
            const rowStyle =
              variance < 0
                ? { backgroundColor: '#fee2e2' }
                : variance > 0
                  ? { backgroundColor: '#fef3c7' }
                  : undefined;
            return (
              <tr key={i} style={rowStyle} data-testid={`wo-item-row-${i}`}>
                <td>{it.line_number}</td>
                <td>
                  <select
                    value={it.nomenclature_id}
                    onChange={(e) => setItem(i, { nomenclature_id: e.target.value })}
                    disabled={!isDraft}
                  >
                    <option value="">—</option>
                    {nomList.data?.items.map((n) => (
                      <option key={n.id} value={n.id}>
                        {n.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    type="number"
                    step="0.001"
                    value={it.expected_quantity}
                    onChange={(e) => setItem(i, { expected_quantity: e.target.value })}
                    disabled={!isDraft}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    step="0.001"
                    value={it.actual_quantity}
                    onChange={(e) => setItem(i, { actual_quantity: e.target.value })}
                    onBlur={() => {
                      if (!isNew && it.id && isDraft) {
                        updateItemMut.mutate({
                          itemId: it.id,
                          patch: { actual_quantity: it.actual_quantity },
                        });
                      }
                    }}
                    disabled={!isDraft && !isCommitteeVerified}
                    data-testid={`wo-actual-input-${i}`}
                  />
                </td>
                <td data-testid={`wo-variance-${i}`}>
                  {variance.toFixed(3)}
                </td>
                <td>
                  <input
                    value={it.variance_reason}
                    onChange={(e) => setItem(i, { variance_reason: e.target.value })}
                    disabled={!isDraft && !isCommitteeVerified}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {isNew && (
        <button
          onClick={() =>
            setForm((f) => ({ ...f, items: [...f.items, newRow(f.items.length + 1)] }))
          }
        >
          + Додати позицію
        </button>
      )}
    </div>
  );
}
